import { Truck, ArrowUpRight, Boxes, Wrench, ConstructionIcon } from "lucide-react";
import { buildWhatsAppLink } from "../lib/contact";

const SERVICES = [
    {
        id: "crane-rental",
        title: "Crane Rental",
        Icon: ConstructionIcon,
        body: "12-ton to 100+ ton mobile, hydraulic & crawler cranes available on daily, weekly or project basis with certified operators.",
        bullets: ["Hydraulic mobile", "Crawler", "Tower assist"],
    },
    {
        id: "heavy-lifting",
        title: "Heavy Lifting",
        Icon: Wrench,
        body: "Precision lifting for plant equipment, transformers, machinery shifting, structural steel & oversized loads up to 100 tons.",
        bullets: ["Rigging plans", "Load charts", "Insured operations"],
    },
    {
        id: "industrial-logistics",
        title: "Industrial Logistics",
        Icon: Truck,
        body: "End-to-end heavy haul logistics — multi-axle trailers, low-beds, route surveys & permit handling across Maharashtra.",
        bullets: ["Multi-axle", "Route surveys", "Permits"],
    },
    {
        id: "on-site-operations",
        title: "On-Site Operations",
        Icon: Boxes,
        body: "Full on-site execution: erection, dismantling, plant relocation, factory shifting and continuous shift operations.",
        bullets: ["Erection", "Plant shifting", "Shift crews"],
    },
];

export const Services = () => {
    return (
        <section
            id="services"
            data-testid="services-section"
            className="relative py-24 lg:py-32 bg-[#0A0A0A]"
        >
            <div className="max-w-7xl mx-auto px-5 lg:px-8">
                {/* Header */}
                <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14 lg:mb-20">
                    <div className="reveal">
                        <div className="flex items-center gap-3 mb-5">
                            <span className="w-10 h-[2px] bg-[#FFC107]" />
                            <span className="font-body text-xs uppercase tracking-[0.32em] text-[#FFC107]">
                                What We Do
                            </span>
                        </div>
                        <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl uppercase text-white leading-[0.9]">
                            Built To Lift.
                            <br />
                            <span className="text-neutral-500">
                                Built To Move.
                            </span>
                        </h2>
                    </div>
                    <p className="reveal max-w-md font-body text-neutral-400 text-base lg:text-lg leading-relaxed">
                        Four core service lines, one promise — show up on time,
                        lift safely, finish the job. Every job site treated like
                        our own.
                    </p>
                </div>

                {/* Bento grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
                    {SERVICES.map(({ id, title, Icon, body, bullets }, idx) => (
                        <a
                            key={id}
                            href={buildWhatsAppLink(
                                `Hi, I'm interested in your ${title} service. Please share details.`,
                            )}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`service-card-${id}`}
                            className="reveal group relative bg-[#0F0F0F] hover:bg-[#FFC107] transition-all duration-500 p-8 lg:p-10 flex flex-col min-h-[420px]"
                            style={{ animationDelay: `${idx * 0.08}s` }}
                        >
                            {/* Index */}
                            <div className="flex items-start justify-between mb-10">
                                <span className="font-display text-sm tracking-widest text-neutral-700 group-hover:text-black/50 transition-colors">
                                    /0{idx + 1}
                                </span>
                                <ArrowUpRight
                                    size={22}
                                    strokeWidth={2}
                                    className="text-neutral-600 group-hover:text-black group-hover:rotate-45 transition-all duration-300"
                                />
                            </div>

                            <Icon
                                size={44}
                                strokeWidth={1.4}
                                className="text-[#FFC107] group-hover:text-black mb-8 transition-colors duration-300"
                            />

                            <h3 className="font-display text-3xl lg:text-4xl uppercase text-white group-hover:text-black leading-tight mb-4 transition-colors duration-300">
                                {title}
                            </h3>

                            <p className="font-body text-sm text-neutral-400 group-hover:text-black/80 leading-relaxed mb-6 transition-colors duration-300">
                                {body}
                            </p>

                            <div className="mt-auto flex flex-wrap gap-2">
                                {bullets.map((b) => (
                                    <span
                                        key={b}
                                        className="font-body text-[10px] uppercase tracking-[0.18em] px-2.5 py-1.5 border border-white/15 group-hover:border-black/40 text-neutral-400 group-hover:text-black/80 transition-colors"
                                    >
                                        {b}
                                    </span>
                                ))}
                            </div>
                        </a>
                    ))}
                </div>
            </div>
        </section>
    );
};
